import { useEffect, useMemo, useState } from "react";
import {
  getPropertyGroups,
  getElementComment,
  formatUnit,
  type ElementRef,
} from "../model/inp/properties.ts";
import type { SwmmProject } from "../model/inp/types.ts";
import type { SimulationResults } from "../model/results/simulationResults.ts";
import { readCoverages, writeCoverages, coveragesFor } from "../model/inp/coverages.ts";
import { readLoadings, writeLoadings, loadingsFor } from "../model/inp/loadings.ts";
import { readLandUses } from "../model/inp/landuses.ts";
import { readPollutants } from "../model/inp/pollutants.ts";
import { readTreatment, writeTreatment } from "../model/inp/treatment.ts";
import { readLidControls } from "../model/inp/lidControls.ts";
import { readLidUsage, writeLidUsage } from "../model/inp/lidUsage.ts";
import { readAquifers } from "../model/inp/aquifers.ts";
import {
  readGroundwater,
  readGwFlowExpressions,
  writeGroundwater,
  writeGwFlowExpressions,
} from "../model/inp/groundwater.ts";
import { readDwf, writeDwf } from "../model/inp/dwf.ts";
import { readInflows, writeInflows } from "../model/inp/inflows.ts";
import { readTimeSeries } from "../model/inp/timeseries.ts";
import { readPatterns } from "../model/inp/patterns.ts";
import { readRdiiInflows, readUnitHydrographs, writeRdiiInflows } from "../model/inp/rdii.ts";
import {
  readInletDesigns,
  readInletUsage,
  writeInletUsage,
  inletUsageFor,
} from "../model/inp/inlets.ts";
import { useT } from "../i18n/hooks.ts";
import {
  fieldLabelKey,
  infiltrationLabelKey,
  kindLabelKey,
  optionLabelKey,
  sectionLabelKey,
} from "../i18n/fieldLabels.ts";
import { CloseIcon } from "./CloseIcon.tsx";
import { IconButton } from "./IconButton.tsx";
import { ResultsChart } from "./ResultsChart.tsx";
import { CoveragesEditor } from "./CoveragesEditor.tsx";
import { LoadingsEditor } from "./LoadingsEditor.tsx";
import { TreatmentEditor } from "./TreatmentEditor.tsx";
import { LidUsageEditor } from "./LidUsageEditor.tsx";
import { GroundwaterEditor } from "./GroundwaterEditor.tsx";
import { DwfEditor } from "./DwfEditor.tsx";
import { InflowsEditor } from "./InflowsEditor.tsx";
import { RdiiEditor } from "./RdiiEditor.tsx";
import { InletUsageEditor } from "./InletUsageEditor.tsx";
import { TraceDownstreamIcon } from "./TraceDownstreamIcon.tsx";
import { TraceUpstreamIcon } from "./TraceUpstreamIcon.tsx";
import type { TraceDirection } from "./useNetworkTrace.ts";

/**
 * Number of significant digits shown in the display (unfocused) form of a
 * property-panel numeric cell, per audit finding: an `.inp` column that
 * types area as `0.6709688 ha` or width as `0.3048 m` should read as
 * `0.671 ha` / `0.3048 m` in the panel — clean enough to skim, without
 * touching the stored value itself. Four significant digits comfortably
 * keeps every SWMM property that ever needs a real difference
 * (roughnesses `0.013`, diameters `0.457 m`, %imperv `42`) unrounded,
 * while trimming the 7-decimal `.inp` noise.
 */
const DISPLAY_SIGNIFICANT_DIGITS = 4;

/**
 * Shortens a raw `.inp` numeric string down to a display-only preview
 * with `DISPLAY_SIGNIFICANT_DIGITS` figures. Anything that doesn't parse
 * cleanly to a finite non-zero number (blank, `-` mid-edit, a leftover
 * scientific placeholder, literal `0`/`0.0`) is passed through verbatim
 * — no display change, no risk of eating whatever the user typed.
 * `useGrouping: false` + `en-US` locale keep the decimal separator as
 * `.` so the string still round-trips through `Number()` the same way
 * it did before this shortening.
 */
function formatDisplayNumeric(value: string): string {
  const trimmed = value.trim();
  if (trimmed === "") return value;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed === 0) return value;
  return new Intl.NumberFormat("en-US", {
    maximumSignificantDigits: DISPLAY_SIGNIFICANT_DIGITS,
    useGrouping: false,
  }).format(parsed);
}

/**
 * An edit here is only forwarded to `onChange` once it parses as a real
 * finite number or is empty; anything else shows an inline error instead.
 * Empty is deliberately its own valid state, never an error — a trailing
 * optional column (e.g. a WEIR/ORIFICE's Barrels/Culvert Code) can
 * legitimately be blank on a real, unedited row. Local `text` state is
 * what's displayed (not `value` directly) so a mid-edit invalid string (a
 * bare "-", a trailing ".") stays visible instead of snapping back;
 * `useEffect` re-syncs it whenever `value` changes for a reason other than
 * this input's own edits.
 *
 * Display-vs-edit precision (audit finding): while unfocused the text is
 * `formatDisplayNumeric(value)` — a 4-significant-digit preview — so a
 * 7-decimal imported column reads as `0.671` in the panel; on focus it
 * flips to the raw `value` so the user always edits from the actual
 * stored string, and the stored value itself is never rounded by this
 * component (a full-precision blur with no edit calls `onChange`
 * nowhere along the way).
 */
function PropertyNumberInput({
  value,
  unit,
  onChange,
  invalidLabel,
  labelId,
  errorId,
}: {
  value: string;
  unit: string;
  onChange: (value: string) => void;
  invalidLabel: string;
  /** The row's `<th>` id — this input has no visible label of its own, so `aria-labelledby` is how a screen reader announces which property this is. */
  labelId: string;
  /** Referenced by `aria-describedby` only while actually invalid. */
  errorId: string;
}) {
  const [text, setText] = useState(() => formatDisplayNumeric(value));
  // Guards the `value` → text sync so a just-parsed round-trip mid-edit
  // (or a re-render from another prop change) can't overwrite the raw
  // text the user is typing, or reformat the focus-time full-precision
  // view back to the truncated preview.
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(formatDisplayNumeric(value));
  }, [value, focused]);
  // Blank counts as valid too — an in-progress edit (still typing, or
  // cleared to be retyped) isn't the same as an actually-invalid number.
  const isValidNumericText = (t: string) => t.trim() === "" || Number.isFinite(Number(t));
  const isValid = isValidNumericText(text);
  return (
    <div className="property-panel-number">
      <div className="property-panel-number-input">
        <input
          type="text"
          inputMode="decimal"
          value={text}
          aria-labelledby={labelId}
          aria-invalid={!isValid}
          aria-describedby={isValid ? undefined : errorId}
          onFocus={() => {
            setFocused(true);
            setText(value);
          }}
          onBlur={() => {
            setFocused(false);
            setText(formatDisplayNumeric(value));
          }}
          onChange={(e) => {
            const next = e.target.value;
            setText(next);
            if (isValidNumericText(next)) onChange(next);
          }}
        />
        {unit && <span className="property-panel-unit">{unit}</span>}
      </div>
      {!isValid && (
        <span id={errorId} className="property-panel-error" role="alert">
          {invalidLabel}
        </span>
      )}
    </div>
  );
}

/** Shared by every `readXxx(project.rawSections)` call below — memoizes the parse against `rawSections`'s own identity, which only changes on a real edit, not on a re-render or a selection change. `read` is a stable top-level import in every call site, never included in the dep array (same convention this codebase uses elsewhere for stable function references). */
function useRawSection<T>(
  read: (raw: ReadonlyMap<string, string[][]>) => T,
  rawSections: ReadonlyMap<string, string[][]>
): T {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => read(rawSections), [rawSections]);
}

/** `useRawSectionEditor`'s own `mutateSection` — one section-level writer wired to an undo-coalescing edit, reused here instead of the 10 separate `onChangeXxx` callbacks this panel used to take (one per sub-editor below), each of which was just `mutateSection(writeXxx)` built in App.tsx. */
type MutateSection = <T>(
  writer: (raw: ReadonlyMap<string, string[][]>, data: T) => ReadonlyMap<string, string[][]>
) => (data: T) => void;

export function PropertyPanel({
  project,
  selected,
  comments,
  onClose,
  onChange,
  mutateSection,
  onBlur,
  results,
  onRunNetworkTrace,
}: {
  project: SwmmProject;
  selected: ElementRef;
  /** The project's own `parseInpComments` map (or its persisted copy) — `undefined` for a project with no captured comments at all (created fresh, or opened before this feature existed). */
  comments?: ReadonlyMap<string, string>;
  onClose: () => void;
  onChange: (section: string, elementName: string, columnIndex: number, value: string) => void;
  mutateSection: MutateSection;
  /** Fired whenever any field inside this panel loses focus, via React's `onBlur` bubbling (native `focusout`, which — unlike plain `blur` — bubbles) — covers every nested editor with one handler. */
  onBlur?: () => void;
  results: SimulationResults | null;
  /** Contextual "trace upstream / downstream from THIS node" action — a
   *  shortcut alongside ToolRail.tsx's own "Herramientas" menu (same two
   *  actions, via the older pick-a-node-on-the-map flow), for when a node
   *  is already selected: skips the pick round trip and shows up right
   *  next to the rest of that node's actions instead. Icon-only, same
   *  compact treatment as every other icon-only action in this panel —
   *  the full-sentence label lives in the tooltip instead (audit
   *  finding: text buttons here read as heavier than the rest of the
   *  panel's own compact controls). */
  onRunNetworkTrace?: (direction: TraceDirection) => void;
}) {
  const t = useT();
  const groups = getPropertyGroups(project, selected);
  const kindKey = kindLabelKey(selected.kind);
  const comment = getElementComment(comments, selected);

  // Each of these editors' own raw `.inp` sections used to be re-parsed from
  // scratch on every render — several of them 2-4x each, once per read call
  // site (a display read plus its own onChange handler's read, or the same
  // section read by more than one editor, e.g. [POLLUTANTS] by 4). All 18
  // depend only on `project.rawSections` (never on `selected`), so they stay
  // fully valid across a selection change too, not just an unrelated
  // re-render — only an actual edit invalidates any of them.
  const rawSections = project.rawSections;
  const landUses = useRawSection(readLandUses, rawSections);
  const pollutants = useRawSection(readPollutants, rawSections);
  const coverages = useRawSection(readCoverages, rawSections);
  const loadings = useRawSection(readLoadings, rawSections);
  const lidControls = useRawSection(readLidControls, rawSections);
  const lidUsage = useRawSection(readLidUsage, rawSections);
  const aquifers = useRawSection(readAquifers, rawSections);
  const groundwaterRows = useRawSection(readGroundwater, rawSections);
  const gwFlowExpressionRows = useRawSection(readGwFlowExpressions, rawSections);
  const treatments = useRawSection(readTreatment, rawSections);
  const dwfInflows = useRawSection(readDwf, rawSections);
  const patterns = useRawSection(readPatterns, rawSections);
  const externalInflows = useRawSection(readInflows, rawSections);
  const timeSeries = useRawSection(readTimeSeries, rawSections);
  const unitHydrographs = useRawSection(readUnitHydrographs, rawSections);
  const rdiiInflows = useRawSection(readRdiiInflows, rawSections);
  const inletDesigns = useRawSection(readInletDesigns, rawSections);
  const inletUsages = useRawSection(readInletUsage, rawSections);

  return (
    <aside className="property-panel" onBlur={onBlur}>
      <div className="property-panel-header">
        <div className="property-panel-title">
          <h2>{selected.name}</h2>
          {/* The element's own kind, as a small badge next to its name
              (UI redesign phase 5) rather than a separate line below it —
              a compact "what this is" next to "which one this is",
              instead of a full sentence's worth of space for one word. */}
          <span className="property-panel-kind-badge">{kindKey ? t(kindKey) : selected.kind}</span>
        </div>
        {/* Contextual trace actions — live feedback: floating between the
            (optional) chart and the property groups below read as
            unanchored "no man's land", especially with no chart to sit
            under. The header is the one thing every element has, chart or
            not, so these actions for THIS element now live right there. */}
        {selected.category === "node" && onRunNetworkTrace && (
          <div className="property-panel-actions">
            <IconButton
              icon={<TraceUpstreamIcon />}
              label={t("networkTrace.traceUpstream")}
              onClick={() => onRunNetworkTrace("upstream")}
            />
            <IconButton
              icon={<TraceDownstreamIcon />}
              label={t("networkTrace.traceDownstream")}
              onClick={() => onRunNetworkTrace("downstream")}
            />
          </div>
        )}
        <button
          className="button-icon property-panel-close"
          onClick={onClose}
          aria-label={t("common.close")}
          title={t("common.close")}
        >
          <CloseIcon />
        </button>
      </div>
      {comment && (
        <p className="property-panel-comment">
          <strong>{t("propertyPanel.description")}:</strong> {comment}
        </p>
      )}
      {results && (
        <ResultsChart results={results} selected={selected} flowUnits={project.flowUnits} />
      )}
      {groups.length === 0 && (
        <p className="property-panel-hint">{t("propertyPanel.noProperties")}</p>
      )}
      {groups.map((group) => {
        // INFILTRATION's label switches on the project's INFILTRATION
        // method, unlike every other group's fixed section-name label.
        const groupLabelKey =
          group.section === "INFILTRATION"
            ? infiltrationLabelKey(group.label)
            : sectionLabelKey(group.section);
        return (
          <section key={group.section} className="property-panel-group">
            <h3>{groupLabelKey ? t(groupLabelKey) : group.label}</h3>
            {/* A single grid (UI redesign phase 5), not a `<table>` per
                group — each group used to be its own separate table, so
                plain browser table layout sized every group's own label
                column differently (a group with one long label pushed its
                own inputs far right of a group with only short ones,
                nothing lined up down the whole panel). `.property-panel-
                field` below is `display: contents` so its own label/value
                children join THIS shared grid directly, giving every group
                the exact same column split without the width-matching
                trick a table needed. Semantics are unaffected — every
                field's accessible name was already wired explicitly via
                `aria-labelledby`/`id` (P1 deep-review item 23), not by
                `<th>`/`<td>` association, so nothing here depends on real
                table markup. */}
            <div className="property-panel-fields">
              {group.properties.map((prop) => {
                const propLabelKey = fieldLabelKey(group.section, prop.label);
                // A value outside its own known option set (an odd import, or
                // a default that doesn't match one of these) still has to
                // render as-is, not silently vanish into the first option.
                const options =
                  prop.options && !prop.options.includes(prop.value)
                    ? [prop.value, ...prop.options]
                    : prop.options;
                const rowId = `property-panel-${group.section}-${prop.columnIndex}`;
                const labelId = `${rowId}-label`;
                const errorId = `${rowId}-error`;
                const labelText = propLabelKey ? t(propLabelKey) : prop.label;
                // Audit finding: labels longer than the 42% column can
                // hold ("Nivel/Tabla/Serie temporal", "%Imperm. sin
                // depresión", "Longitud de bordillo") wrapped to two
                // lines while the input to their right sat in a
                // half-empty cell. 18 chars is the widest label that
                // reliably fits the current column in every translation
                // (`Compuerta de marea` = 18, `Impervious slope` = 16
                // for example) — anything longer switches to the
                // label-above / input-below layout below via
                // `-full` grid-column overrides. Uses `Array.from`
                // instead of `.length` so a multi-byte grapheme (an
                // accented "á") counts as one, matching how it actually
                // reads on screen.
                const longLabel = Array.from(labelText).length > 18;
                const labelClass = longLabel
                  ? "property-panel-field-label property-panel-field-label-full"
                  : "property-panel-field-label";
                const valueClass = longLabel
                  ? "property-panel-field-value property-panel-field-value-full"
                  : "property-panel-field-value";
                return (
                  <div className="property-panel-field" key={prop.label}>
                    <span id={labelId} className={labelClass}>
                      {labelText}
                    </span>
                    <div className={valueClass}>
                      {options ? (
                        <select
                          value={prop.value}
                          aria-labelledby={labelId}
                          onChange={(e) =>
                            onChange(group.section, selected.name, prop.columnIndex, e.target.value)
                          }
                        >
                          {options.map((option) => {
                            const optionKey = optionLabelKey(option);
                            return (
                              <option key={option} value={option}>
                                {optionKey ? t(optionKey) : option}
                              </option>
                            );
                          })}
                        </select>
                      ) : prop.unitKind ? (
                        <PropertyNumberInput
                          value={prop.value}
                          unit={formatUnit(prop.unitKind, project.flowUnits)}
                          invalidLabel={t("propertyPanel.invalidNumber")}
                          labelId={labelId}
                          errorId={errorId}
                          onChange={(value) =>
                            onChange(group.section, selected.name, prop.columnIndex, value)
                          }
                        />
                      ) : (
                        <input
                          type="text"
                          value={prop.value}
                          aria-labelledby={labelId}
                          onChange={(e) =>
                            onChange(group.section, selected.name, prop.columnIndex, e.target.value)
                          }
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
      {selected.category === "subcatchment" && (
        <CoveragesEditor
          subcatchmentName={selected.name}
          landUses={landUses}
          coverages={coveragesFor(coverages, selected.name)}
          onChange={(rows) => {
            const others = coverages.filter((c) => c.subcatchment !== selected.name);
            mutateSection(writeCoverages)([...others, ...rows]);
          }}
        />
      )}
      {selected.category === "subcatchment" && (
        <LoadingsEditor
          subcatchmentName={selected.name}
          pollutants={pollutants}
          loadings={loadingsFor(loadings, selected.name)}
          onChange={(rows) => {
            const others = loadings.filter((l) => l.subcatchment !== selected.name);
            mutateSection(writeLoadings)([...others, ...rows]);
          }}
        />
      )}
      {selected.category === "subcatchment" && (
        <LidUsageEditor
          subcatchmentName={selected.name}
          lidControls={lidControls}
          usage={lidUsage.filter((u) => u.subcatchment === selected.name)}
          onChange={(rows) => {
            const others = lidUsage.filter((u) => u.subcatchment !== selected.name);
            mutateSection(writeLidUsage)([...others, ...rows]);
          }}
        />
      )}
      {selected.category === "subcatchment" && (
        <GroundwaterEditor
          subcatchmentName={selected.name}
          aquifers={aquifers}
          nodeNames={project.nodes.map((n) => n.name)}
          groundwater={groundwaterRows.find((g) => g.subcatchment === selected.name) ?? null}
          onChangeGroundwater={(gw) => {
            const others = groundwaterRows.filter((g) => g.subcatchment !== selected.name);
            mutateSection(writeGroundwater)(gw ? [...others, gw] : others);
          }}
          flowExpressions={gwFlowExpressionRows.filter((e) => e.subcatchment === selected.name)}
          onChangeFlowExpressions={(rows) => {
            const others = gwFlowExpressionRows.filter((e) => e.subcatchment !== selected.name);
            mutateSection(writeGwFlowExpressions)([...others, ...rows]);
          }}
        />
      )}
      {selected.category === "node" && (
        <TreatmentEditor
          nodeName={selected.name}
          pollutants={pollutants}
          treatments={treatments.filter((tr) => tr.node === selected.name)}
          onChange={(rows) => {
            const others = treatments.filter((tr) => tr.node !== selected.name);
            mutateSection(writeTreatment)([...others, ...rows]);
          }}
        />
      )}
      {selected.category === "node" && (
        <DwfEditor
          nodeName={selected.name}
          pollutants={pollutants}
          patterns={patterns}
          inflows={dwfInflows.filter((i) => i.node === selected.name)}
          onChange={(rows) => {
            const others = dwfInflows.filter((i) => i.node !== selected.name);
            mutateSection(writeDwf)([...others, ...rows]);
          }}
        />
      )}
      {selected.category === "node" && (
        <InflowsEditor
          nodeName={selected.name}
          pollutants={pollutants}
          patterns={patterns}
          timeSeries={timeSeries}
          inflows={externalInflows.filter((i) => i.node === selected.name)}
          onChange={(rows) => {
            const others = externalInflows.filter((i) => i.node !== selected.name);
            mutateSection(writeInflows)([...others, ...rows]);
          }}
        />
      )}
      {selected.category === "node" && (
        <RdiiEditor
          nodeName={selected.name}
          unitHydrographs={unitHydrographs}
          inflow={rdiiInflows.find((i) => i.node === selected.name) ?? null}
          onChange={(inflow) => {
            const others = rdiiInflows.filter((i) => i.node !== selected.name);
            mutateSection(writeRdiiInflows)(inflow ? [...others, inflow] : others);
          }}
        />
      )}
      {selected.category === "link" && selected.kind === "conduit" && (
        <InletUsageEditor
          linkName={selected.name}
          inletDesigns={inletDesigns}
          usage={inletUsageFor(inletUsages, selected.name)}
          nodeNames={project.nodes.map((n) => n.name)}
          onChange={(rows) => {
            const others = inletUsages.filter((u) => u.link !== selected.name);
            mutateSection(writeInletUsage)([...others, ...rows]);
          }}
        />
      )}
    </aside>
  );
}
